import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { S } from '../styles.js';
import { labelForSection, validateSection, calculateProgress } from './utils.js';
import { SECTION_KEYS, DOMESTIC_SECTION_KEYS } from '../../../constants.js';

export function Stepper({ 
  currentStep, 
  onStepClick, 
  formState, 
  completedSteps = [],
  category = "international"
}) {
  const sectionKeys = category === "domestic" ? DOMESTIC_SECTION_KEYS : SECTION_KEYS;
  const progress = calculateProgress(currentStep, sectionKeys.length);

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: 4,
        background: '#e5e7eb',
        borderRadius: 2,
        marginBottom: 20,
        overflow: 'hidden'
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{
            height: '100%',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2
          }}
        />
      </div>

      {/* Step indicators */}
      <div style={S.stepper}>
        {sectionKeys.map((key, index) => {
          const isActive = index === currentStep;
          const isCompleted = completedSteps.includes(index);
          const isClickable = index <= currentStep || isCompleted;
          const validation = validateSection(key, formState);
          const hasErrors = !validation.valid;

          return (
            <StepPill
              key={key}
              index={index}
              label={labelForSection(key)}
              isActive={isActive}
              isCompleted={isCompleted}
              isClickable={isClickable}
              hasErrors={hasErrors}
              onClick={() => isClickable && onStepClick(index)}
            />
          );
        })}
      </div>

      {/* Step info */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          textAlign: 'center',
          marginTop: 16,
          padding: '12px 20px',
          background: '#f8faff',
          borderRadius: 8,
          border: '1px solid #e0e7ff'
        }}
      >
        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>
          Step {currentStep + 1} of {sectionKeys.length}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
          {labelForSection(sectionKeys[currentStep])}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
          {progress}% Complete
        </div>
      </motion.div>
    </div>
  );
}

function StepPill({ 
  index, 
  label, 
  isActive, 
  isCompleted, 
  isClickable, 
  hasErrors, 
  onClick 
}) {
  const getStepState = () => {
    if (isCompleted) return 'completed';
    if (isActive) return 'active';
    if (hasErrors) return 'error';
    return 'default';
  };

  const stepState = getStepState();

  const getStepStyles = () => {
    const baseStyles = {
      padding: "12px 16px",
      borderRadius: 25,
      border: "2px solid",
      fontSize: 13,
      fontWeight: 600,
      cursor: isClickable ? "pointer" : "not-allowed",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 'fit-content',
      opacity: isClickable ? 1 : 0.6
    };

    const stateStyles = {
      completed: {
        background: "#10b981",
        color: "#fff",
        borderColor: "#10b981",
        boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)"
      },
      active: {
        background: "linear-gradient(135deg, #056ec4 0%, #192b64 100%)",
        color: "#fff",
        borderColor: "transparent",
        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)"
      },
      error: {
        background: "#fef2f2",
        color: "#dc2626",
        borderColor: "#fecaca",
        boxShadow: "0 2px 8px rgba(220, 38, 38, 0.1)"
      },
      default: {
        background: "#fff",
        color: "#6b7280",
        borderColor: "#d1d5db",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
      }
    };

    return { ...baseStyles, ...stateStyles[stepState] };
  };

  return (
    <motion.button
      type="button"
      onClick={onClick}
      style={getStepStyles()}
      whileHover={isClickable ? {
        scale: 1.05,
        boxShadow: stepState === 'active' 
          ? "0 6px 20px rgba(102, 126, 234, 0.4)"
          : stepState === 'completed'
          ? "0 6px 20px rgba(16, 185, 129, 0.4)"
          : "0 4px 15px rgba(0,0,0,0.1)"
      } : {}}
      whileTap={isClickable ? { scale: 0.95 } : {}}
      disabled={!isClickable}
    >
      <StepIcon 
        stepNumber={index + 1} 
        isCompleted={isCompleted} 
        hasErrors={hasErrors}
        isActive={isActive}
      />
      <span style={{ whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {hasErrors && !isActive && !isCompleted && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#dc2626',
            marginLeft: 4
          }}
        />
      )}
    </motion.button>
  );
}

function StepIcon({ stepNumber, isCompleted, hasErrors, isActive }) {
  const iconStyles = {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 'bold',
    flexShrink: 0
  };

  if (isCompleted) {
    return (
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        style={{
          ...iconStyles,
          background: 'rgba(255,255,255,0.2)',
          color: 'inherit'
        }}
      >
        <Check size={12} />
      </motion.div>
    );
  }

  if (hasErrors && !isActive) {
    return (
      <div style={{
        ...iconStyles,
        background: '#dc2626',
        color: '#fff'
      }}>
        !
      </div>
    );
  }

  return (
    <div style={{
      ...iconStyles,
      background: isActive ? 'rgba(255,255,255,0.2)' : 'transparent',
      color: 'inherit',
      border: isActive ? 'none' : '1px solid currentColor'
    }}>
      {stepNumber}
    </div>
  );
}

// Mini stepper for mobile/compact view
export function MiniStepper({ currentStep, totalSteps }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: '8px 16px',
      background: '#f8faff',
      borderRadius: 20,
      border: '1px solid #e0e7ff',
      fontSize: 14,
      fontWeight: 600,
      color: '#374151'
    }}>
      <span>{currentStep + 1}</span>
      <span style={{ color: '#9ca3af' }}>of</span>
      <span>{totalSteps}</span>
    </div>
  );
}